import { cn, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { NCAContributionsAndEarnings } from '@/api/rest/resources/types/organization';
import { WidgetType } from '@/api/rest/resources/types/widget';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { minMaxAxisDomain } from '@/components/Charts/utils';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { getDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { useNaturalCapitalAccount } from '@/hooks/useNaturalCapitalAccount';
import { isNullish } from '@/utils/booleans';

const CHART_COLORS = {
  totalContributions: '#8DE4E6',
  totalEarnings: '#64B6D3',
} as const;

export const useEarningsChartConfiguration = () => {
  const data = useNaturalCapitalAccount().data.land_type_contributions_and_earnings;
  const { t } = useTranslation();
  const orgCurrency = useDefaultCurrency();

  const noChartData = isNullish(data) || data.length === 0;

  // Extract the latest year from reference dates
  const latestYear = data?.length
    ? Math.max(
        ...data.flatMap((item) => [
          new Date(item.values.total_contributions_reference_date).getFullYear(),
          new Date(item.values.total_earnings_reference_date).getFullYear(),
        ]),
      )
    : null;

  const commonClasses = 'border-none typography-h3 px-0';
  const LEGEND_CLASS = 'bg-neutral-black-4 p-2 rounded-lg';

  const currency = data[0]?.unit ?? orgCurrency;

  const formatter = (value: number) => getDisplayCurrency(value, window.navigator.language, currency);

  const chartData = data.map((item) => ({
    ...item,
    name: t(`global.plotTypes.${item.name}`),
  }));

  const legendItems: ChartLegendBarItem[] = [
    {
      className: LEGEND_CLASS,
      label: t('earnings.totalContributions'),
      color: CHART_COLORS.totalContributions,
    },
    {
      className: LEGEND_CLASS,
      label: t('earnings.totalEarnings'),
      color: CHART_COLORS.totalEarnings,
    },
  ];

  return {
    name: 'earnings_chart_card',
    type: WidgetType.o_card,
    visible: !noChartData,
    relatedData: {
      styles: {
        root: cn('h-full', commonClasses),
        header: commonClasses,
        body: 'h-full flex flex-col justify-center p-2 border border-neutral-black-12 rounded-2xl',
      },
      header: t('global.widget.earnings', { year: latestYear }),
    },
    widgets: [
      {
        name: 'earnings_chart_graph',
        type: WidgetType.o_composite_graph,
        visible: true,
        relatedData: {
          'data-testid': 'earnings-chart',
          data: chartData,
          chartConfig: [
            {
              shape: ChartShape.bar,
              dataKey: 'values.total_contributions',
              label: t('earnings.totalContributions'),
              color: CHART_COLORS.totalContributions,
              fill: CHART_COLORS.totalContributions,
              key: 'totalContributions',
              id: 'totalContributions',
              stackId: 'earnings',
              barSize: 100,
              barColors: [CHART_COLORS.totalContributions],
              showLabel: true,
              formatter,
            },

            {
              shape: ChartShape.bar,
              dataKey: 'values.total_earnings_value',
              label: t('earnings.totalEarnings'),
              color: CHART_COLORS.totalEarnings,
              fill: CHART_COLORS.totalEarnings,
              key: 'totalEarnings',
              id: 'totalEarnings',
              stackId: 'earnings',
              barSize: 100,
              barColors: [CHART_COLORS.totalEarnings],
              showLabel: true,
              formatter,
            },
          ],
          legendItems,
          tooltip: CustomTooltip,
          xAxisProps: {
            dataKey: 'name',
            type: 'category',
          },
          yAxisProps: {
            label: currency,
            domain: minMaxAxisDomain(1, 1.2),
          },
        },
      },
    ],
  };
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; payload: NCAContributionsAndEarnings }>;
  label?: string;
};

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) return null;

  const contributionsData = payload.find((item) => item.dataKey === 'values.total_contributions');
  const earningsData = payload.find((item) => item.dataKey === 'values.total_earnings_value');

  const contributionsValue =
    contributionsData?.value !== null && contributionsData?.value !== undefined
      ? getDisplayCurrency(contributionsData.value, window.navigator.language, contributionsData?.payload?.unit)
      : t('global.analysis.noData');

  const earningsValue =
    earningsData?.value !== null && earningsData?.value !== undefined
      ? getDisplayCurrency(earningsData.value, window.navigator.language, contributionsData?.payload?.unit)
      : t('global.analysis.noData');

  const renderTooltipRow = (rowLabel: string, value: string | null) => {
    if (!value) return null;

    return (
      <Stack direction='row' spacing={8} className='justify-between'>
        <ChartTooltipTextPrimary>{rowLabel}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{value}</ChartTooltipTextPrimary>
      </Stack>
    );
  };

  const CONTRIBUTIONS_LABEL = t(`earnings.totalContributions`);
  const EARNINGS_LABEL = t(`earnings.totalEarnings`);

  return (
    <ChartTooltip className='ml-1'>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{label}</ChartTooltipTextSecondary>
      </Stack>
      <Stack spacing={2} direction='col'>
        {renderTooltipRow(EARNINGS_LABEL, earningsValue)}
        {renderTooltipRow(CONTRIBUTIONS_LABEL, contributionsValue)}
      </Stack>
    </ChartTooltip>
  );
};
